import React from "react";
import {
  Box, Checkbox, Collapse, FormControlLabel, IconButton, InputAdornment,
  Stack, TextField, Tooltip, Typography, Grid,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ColorPicker from "../../../../Components/ColorPicker/ColorPicker";
import OutcomeStateToggle, { OutcomeState } from "./OutcomeStateToggle";
import { LegacyTokens, TypeScale } from "../../../../theme/b13Tokens";
import { B13, B13Ink, tint12 } from "../../../../theme/b13Tokens";
import { OutcomeMode } from "./outcomeModeConstants";

// Batch 6 §6.3 — the bordered white card that replaces the current tight
// stacked row layout. Visible surface = swatch tile, level name input,
// outcome-state toggle, reorder, delete. Everything else lives inside
// the Advanced settings collapse.

export type PromptLevelCardValue = {
  id?: number;
  levelName: string;
  colour: string;
  outcomeState: OutcomeState;
  // Advanced-settings fields kept fully functional:
  shortCode: string;
  score: number;                    // Independence % (successful_unsuccessful mode)
  description: string;
  isActive: boolean;
  countsAsIndependent: boolean;     // synced with outcomeState==='successful'
  orderNumber: number;
  // Batch 6A — Weighted mode's per-level score (0-100%). Distinct from
  // `score` so mode switches never silently reinterpret one number as
  // the other. Null = "never set in Weighted mode".
  weightedScore: number | null;
  // Drag-reorder client key — assigned by the editor body, never
  // persisted. Required for @dnd-kit's SortableContext id AND for the
  // React key on the mapped card so an in-flight edit survives a
  // reorder (row identity, not row position). Both consumers whitelist
  // the fields they send, so this never reaches the API.
  _dndId?: string;
};

export type PromptLevelCardProps = {
  value: PromptLevelCardValue;
  index: number;
  total: number;
  nameError?: string | null;
  outcomeMode: OutcomeMode;
  onChange: (patch: Partial<PromptLevelCardValue>) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  readOnly?: boolean;
  registerNameRef?: (el: HTMLInputElement | null) => void;
  // dnd-kit sortable id — pass value._dndId or a stable string. When
  // omitted the card renders without drag (single-card previews, tests).
  sortableId?: string;
};

export default function PromptLevelCard({
  value, index, total, nameError, outcomeMode,
  onChange, onMoveUp, onMoveDown, onDuplicate, onDelete,
  readOnly = false,
  registerNameRef,
  sortableId,
}: PromptLevelCardProps) {
  const [advOpen, setAdvOpen] = React.useState(false);
  const disableUp = index === 0;
  const disableDown = index === total - 1;

  // useSortable is a no-op outside a SortableContext, but calling it
  // conditionally would violate rules-of-hooks. Always call; ignore
  // the returned bindings when sortableId is not provided.
  const sortable = useSortable({ id: sortableId ?? `promptlevel-${index}`, disabled: readOnly });
  const dragActive = Boolean(sortableId) && sortable.isDragging;
  const style: React.CSSProperties = sortableId
    ? {
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
        opacity: dragActive ? 0.7 : undefined,
        zIndex: dragActive ? 2 : undefined,
        // Elevate the card only while being dragged so it visually
        // detaches from the stack. No shadow at rest.
        boxShadow: dragActive ? "0 8px 20px rgba(0,0,0,0.12)" : undefined,
      }
    : {};

  return (
    <Box
      ref={sortableId ? sortable.setNodeRef : undefined}
      style={style}
      data-testid="prompt-level-card"
      sx={{
        border: `1px solid ${dragActive ? B13.primary : LegacyTokens.line}`,
        borderRadius: `${LegacyTokens.radius.card}px`,
        backgroundColor: LegacyTokens.surface,
        p: 2,
        opacity: value.isActive ? 1 : 0.6,
      }}
    >
      <Grid container spacing={2} alignItems="center" wrap="nowrap">
        {/* (0) Drag handle — first cell so it lives at the card's left
             edge, before the colour swatch. Listeners are attached ONLY
             to this handle, not the whole card, so text selection in
             the name field and pointer interaction with the swatch /
             toggle / delete button all continue to work normally.
             Space or Enter picks the card up; arrows move it while the
             KeyboardSensor is active; Space or Enter drops; Escape
             cancels — bindings come from @dnd-kit's default keyboard
             coordinate getter (sortableKeyboardCoordinates). */}
        {sortableId && (
          <Grid item sx={{ flexShrink: 0 }}>
            <Tooltip title="Drag to reorder (Space to pick up, arrows to move, Space to drop)" arrow>
              <span>
                <IconButton
                  size="small"
                  aria-label={`Reorder level ${index + 1}`}
                  disabled={readOnly}
                  ref={sortable.setActivatorNodeRef}
                  {...sortable.attributes}
                  {...sortable.listeners}
                  sx={{
                    color: LegacyTokens.ink3,
                    cursor: dragActive ? "grabbing" : "grab",
                    touchAction: "none",
                    "&:hover": { color: B13.primary },
                  }}
                >
                  <DragIndicatorIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        )}

        {/* (a) Colour swatch tile */}
        <Grid item sx={{ flexShrink: 0 }}>
          <ColorPicker
            value={value.colour || LegacyTokens.blue}
            onChange={(hex) => onChange({ colour: hex })}
            disabled={readOnly}
            size={70}
            ariaLabel={`Colour for level ${index + 1}`}
          />
        </Grid>

        {/* (b) Level name */}
        <Grid item xs sx={{ minWidth: 0 }}>
          <Typography sx={{ ...TypeScale.fieldLabel, mb: 0.5, display: "block", color: nameError ? LegacyTokens.red : LegacyTokens.ink3 }}>
            Prompt level
          </Typography>
          <TextField
            fullWidth
            variant="standard"
            placeholder="e.g. Full Physical"
            value={value.levelName}
            disabled={readOnly}
            onChange={(e) => onChange({ levelName: e.target.value })}
            inputRef={registerNameRef}
            error={Boolean(nameError)}
            helperText={nameError || " "}
            InputProps={{
              sx: {
                fontSize: 18,
                fontWeight: 500,
                color: LegacyTokens.ink,
                "&:before": { borderBottom: `1px solid ${nameError ? LegacyTokens.red : LegacyTokens.line}` },
                "&:after":  { borderBottomWidth: 2, borderBottomColor: nameError ? LegacyTokens.red : B13.primary },
              },
            }}
            FormHelperTextProps={{ sx: { color: nameError ? LegacyTokens.red : LegacyTokens.ink3, minHeight: 18 } }}
          />
        </Grid>

        {/* (c) Outcome column — Batch 6A mode-conditional.
             - successful_unsuccessful: OutcomeStateToggle (Batch 6 build)
             - weighted: Score (0-100%) numeric field with clear (×)
             - rating_scale: renders NO outcome column at all per user
               instruction — no placeholder, no "TODO", no dev copy.
               The card degrades to swatch + name + chevrons + delete +
               advanced settings until the client supplies the rating-
               scale card anatomy. */}
        {outcomeMode === "successful_unsuccessful" && (
          <Grid item sx={{ flexShrink: 0 }}>
            <OutcomeStateToggle
              value={value.outcomeState}
              disabled={readOnly}
              onChange={(next) =>
                onChange({
                  outcomeState: next,
                  countsAsIndependent: next === "successful",
                })
              }
            />
          </Grid>
        )}

        {outcomeMode === "weighted" && (
          <Grid item sx={{ flexShrink: 0, minWidth: 220 }}>
            <Typography sx={{ ...TypeScale.fieldLabel, mb: 0.5, display: "block" }}>
              Score (0 - 100%)
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                value={value.weightedScore ?? ""}
                disabled={readOnly}
                inputProps={{ min: 0, max: 100, step: 1 }}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === "") {
                    onChange({ weightedScore: null });
                  } else {
                    const n = Math.max(0, Math.min(100, Number(raw)));
                    onChange({ weightedScore: n });
                  }
                }}
                placeholder="N/A"
              />
              <Tooltip title="Clear (set N/A)" arrow>
                <span>
                  <IconButton
                    size="small"
                    disabled={readOnly || value.weightedScore === null}
                    onClick={() => onChange({ weightedScore: null })}
                    aria-label="Clear score"
                    sx={{
                      color: LegacyTokens.ink3,
                      border: `1px solid ${LegacyTokens.line}`,
                      borderRadius: "50%",
                      width: 24, height: 24,
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Grid>
        )}

        {/* rating_scale renders no outcome column — intentional blank. */}

        {/* (d) Reorder */}
        <Grid item sx={{ flexShrink: 0 }}>
          <Stack spacing={0}>
            <Tooltip title="Move up" arrow>
              <span>
                <IconButton
                  size="small"
                  aria-label="Move up"
                  disabled={readOnly || disableUp}
                  onClick={onMoveUp}
                  sx={{ color: LegacyTokens.ink3 }}
                >
                  <KeyboardArrowUpIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Move down" arrow>
              <span>
                <IconButton
                  size="small"
                  aria-label="Move down"
                  disabled={readOnly || disableDown}
                  onClick={onMoveDown}
                  sx={{ color: LegacyTokens.ink3 }}
                >
                  <KeyboardArrowDownIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Grid>

        {/* (e) Delete */}
        <Grid item sx={{ flexShrink: 0 }}>
          <Tooltip title={total === 1 ? "At least one level required" : "Delete level"} arrow>
            <span>
              <IconButton
                aria-label="Delete level"
                disabled={readOnly || total === 1}
                onClick={onDelete}
                sx={{ color: LegacyTokens.red, "&:hover": { backgroundColor: LegacyTokens.redSoft } }}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
      </Grid>

      {/* (f) Advanced settings */}
      <Box
        onClick={() => setAdvOpen((v) => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setAdvOpen((v) => !v); } }}
        sx={{
          mt: 1,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          fontSize: 12,
          fontWeight: 600,
          color: LegacyTokens.ink3,
          cursor: "pointer",
          userSelect: "none",
          "&:hover": { color: B13.primary },
        }}
      >
        {advOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        Advanced settings
      </Box>

      <Collapse in={advOpen} unmountOnExit>
        <Box sx={{ mt: 1.5, pl: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth size="small" label="Short Code"
                value={value.shortCode}
                disabled={readOnly}
                inputProps={{ maxLength: 3, style: { textTransform: "uppercase" } }}
                onChange={(e) => onChange({ shortCode: e.target.value.slice(0, 3) })}
                onBlur={(e) => onChange({ shortCode: (e.target.value || "").toUpperCase() })}
                helperText="Max 3 characters"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth size="small" type="number" label="Independence %"
                // T63 Part E §25 — explicit contract: blank equals 0
                // ("full support"), NOT "unset". Backend coerces null
                // to 0 on save (PromptLevelTemplateController::save
                // line 412) so the placeholder + helper text make the
                // stored value obvious before the user clicks Save.
                value={value.score}
                disabled={readOnly}
                onChange={(e) => {
                  const raw = e.target.value;
                  // Preserve empty string in the input while the user
                  // is editing; only clamp on numeric input.
                  const n = raw === "" ? 0 : Math.min(100, Math.max(0, Number(raw)));
                  onChange({ score: n });
                }}
                InputProps={{
                  inputProps: { min: 0, max: 100 },
                  endAdornment: <InputAdornment position="end">/ 100</InputAdornment>,
                }}
                InputLabelProps={{ shrink: true }}
                placeholder="0"
                helperText="Blank saves as 0. 0 = full support, 100 = fully independent."
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Stack direction="row" spacing={1} sx={{ mt: { xs: 0, sm: 1 } }}>
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={value.isActive}
                      disabled={readOnly}
                      onChange={(e) => onChange({ isActive: e.target.checked })}
                    />
                  }
                  label={<Typography sx={{ fontSize: 13, color: LegacyTokens.ink2 }}>Active</Typography>}
                />
                <Tooltip title="Duplicate level" arrow>
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Duplicate level"
                      disabled={readOnly}
                      onClick={onDuplicate}
                      sx={{ color: LegacyTokens.ink3 }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" label="Description"
                value={value.description}
                disabled={readOnly}
                onChange={(e) => onChange({ description: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Box>
  );
}
